/**
 * PixiJS board for Quadratic Gallery (二次函数图鉴): paints one 小问 scene
 * of a problem inside a Cartesian coordinate system —
 *
 *   integer grid (faint) -> x/y axes with arrows and tick numbers ->
 *   translucent fills (right trapezoids) -> curves sampled as glowing
 *   polylines (parabolas / lines / vertical symmetry axes) -> auxiliary
 *   dashed segments -> right-angle marks -> labelled key points.
 *
 * The projection fits the scene's view window (explicit `scene.view` or the
 * bounding box of its points) into the canvas with a uniform scale and the
 * y axis mirrored (maths coordinates up). Everything is static: `show`
 * swaps the scene and repaints once.
 */

import { Container, Graphics, Text } from "pixi.js";

import { evalLine, evalQuad, pt } from "../math/func";
import type { Pt } from "../math/func";
import type { CurveDef, SceneDef, Tone, ViewWindow } from "../problems/types";

/** Site palette. */
const COLOR_GRID = 0x1a2740;
const COLOR_AXIS = 0x3d5378;
const COLOR_TICK = 0x8fa3c8;
const COLOR_LABEL = 0xeaf0fb;
const COLOR_EMPHASIS = 0xff3344;
const TONE_COLOR: Readonly<Record<Tone, number>> = {
  cyan: 0x00f0ff,
  gold: 0xffd700,
  red: 0xff3344,
  green: 0x00ff88,
  dim: 0x8fa3c8,
};

const VIEW_PAD_PX = 44;
const CURVE_SAMPLES = 220;

/** World units -> screen pixels: uniform scale + offset, y mirrored. */
interface ViewTransform {
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

/** The slice of a PixiJS Application the board needs. */
export interface BoardHost {
  readonly stage: Container;
  readonly screen: { readonly width: number; readonly height: number };
}

/** Mutable dash state threaded through a dashed polyline. */
interface DashState {
  drawing: boolean;
  remain: number;
}

/** Stroke one piece of a dashed polyline, keeping dash phase across pieces. */
function dashBetween(g: Graphics, p0: Pt, p1: Pt, state: DashState, dash: number, gap: number): void {
  const length = Math.hypot(p1.x - p0.x, p1.y - p0.y);
  if (length < 1e-9) {
    return;
  }
  const ux = (p1.x - p0.x) / length;
  const uy = (p1.y - p0.y) / length;
  let s = 0;
  while (s < length - 1e-9) {
    const step = Math.min(state.remain, length - s);
    const e = s + step;
    if (state.drawing) {
      g.moveTo(p0.x + ux * s, p0.y + uy * s).lineTo(p0.x + ux * e, p0.y + uy * e);
    }
    s = e;
    state.remain -= step;
    if (state.remain <= 1e-9) {
      state.drawing = !state.drawing;
      state.remain = state.drawing ? dash : gap;
    }
  }
}

/** Stroke a dashed polyline (screen px). */
function dashPolyline(g: Graphics, pts: readonly Pt[], dash: number, gap: number): void {
  const state: DashState = { drawing: true, remain: dash };
  for (let i = 1; i < pts.length; i += 1) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    if (p0 !== undefined && p1 !== undefined) {
      dashBetween(g, p0, p1, state, dash, gap);
    }
  }
}

/** A small right-angle square at v, arms pointing towards f and t (screen px). */
function rightAngleSquare(g: Graphics, v: Pt, f: Pt, t: Pt, size: number): void {
  const lf = Math.hypot(f.x - v.x, f.y - v.y);
  const lt = Math.hypot(t.x - v.x, t.y - v.y);
  if (lf < 1e-6 || lt < 1e-6) {
    return;
  }
  const ux = ((f.x - v.x) / lf) * size;
  const uy = ((f.y - v.y) / lf) * size;
  const wx = ((t.x - v.x) / lt) * size;
  const wy = ((t.y - v.y) / lt) * size;
  g.moveTo(v.x + ux, v.y + uy)
    .lineTo(v.x + ux + wx, v.y + uy + wy)
    .lineTo(v.x + wx, v.y + wy);
}

/** Compact coordinate text: integers stay integers, halves keep one decimal. */
function fmtCoord(n: number): string {
  if (Math.abs(n - Math.round(n)) < 1e-9) {
    return String(Math.round(n));
  }
  if (Math.abs(n * 2 - Math.round(n * 2)) < 1e-9) {
    return n.toFixed(1);
  }
  return n.toFixed(2);
}

export class Board {
  /** Board root, added to the host's stage. */
  readonly root: Container;

  private readonly host: BoardHost;
  private scene: SceneDef | null = null;
  private view: ViewWindow = { xMin: -1, xMax: 1, yMin: -1, yMax: 1 };
  private transform: ViewTransform = { scale: 1, offsetX: 0, offsetY: 0 };

  private readonly gridG = new Graphics();
  private readonly axisG = new Graphics();
  private readonly fillG = new Graphics();
  private readonly curveG = new Graphics();
  private readonly segG = new Graphics();
  private readonly markG = new Graphics();
  private readonly labelLayer = new Container();

  constructor(host: BoardHost) {
    this.host = host;
    this.root = new Container();
    this.root.label = "quad-gallery-board";
    this.labelLayer.label = "quad-gallery-labels";
    this.root.addChild(this.gridG, this.axisG, this.fillG, this.curveG, this.segG, this.markG, this.labelLayer);
    host.stage.addChild(this.root);
  }

  /** Current world -> screen projection. */
  worldToScreen(point: Pt): Pt {
    return {
      x: point.x * this.transform.scale + this.transform.offsetX,
      y: this.transform.offsetY - point.y * this.transform.scale,
    };
  }

  /** Swap in a scene and paint it. */
  show(scene: SceneDef): void {
    this.scene = scene;
    this.view = scene.view ?? this.autoView(scene);
    this.relayout(this.host.screen.width, this.host.screen.height);
  }

  /** Re-fit the projection to a new viewport and repaint. */
  relayout(screenW: number, screenH: number): void {
    if (this.scene === null) {
      return;
    }
    const spanX = Math.max(1e-6, this.view.xMax - this.view.xMin);
    const spanY = Math.max(1e-6, this.view.yMax - this.view.yMin);
    const scale = Math.min(
      (screenW - 2 * VIEW_PAD_PX) / spanX,
      (screenH - 2 * VIEW_PAD_PX) / spanY,
    );
    const cx = (this.view.xMin + this.view.xMax) / 2;
    const cy = (this.view.yMin + this.view.yMax) / 2;
    this.transform = {
      scale,
      offsetX: screenW / 2 - cx * scale,
      offsetY: screenH / 2 + cy * scale,
    };
    this.paint();
  }

  /* ------------------------------------------------------------- view */

  /** Bounding-box view of the scene's points (used when scene.view is absent). */
  private autoView(scene: SceneDef): ViewWindow {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const eat = (p: Pt): void => {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    };
    for (const p of scene.points) {
      eat(p.at);
    }
    for (const s of scene.segments ?? []) {
      eat(s.a);
      eat(s.b);
    }
    if (!Number.isFinite(minX)) {
      return { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
    }
    const padX = Math.max(0.5, (maxX - minX) * 0.12);
    const padY = Math.max(0.5, (maxY - minY) * 0.12);
    return { xMin: minX - padX, xMax: maxX + padX, yMin: minY - padY, yMax: maxY + padY };
  }

  /** Integer tick step keeping at most ~12 labels per axis. */
  private tickStep(span: number): number {
    return Math.max(1, Math.ceil(span / 12));
  }

  /* ----------------------------------------------------------- painting */

  private paint(): void {
    const scene = this.scene;
    if (scene === null) {
      return;
    }
    const view = this.view;
    this.paintGrid(view);
    this.paintAxes(view);

    const fills = this.fillG.clear();
    for (const fill of scene.fills ?? []) {
      const color = TONE_COLOR[fill.tone];
      const pts = fill.points.map((p) => this.worldToScreen(p));
      const first = pts[0];
      if (first === undefined) {
        continue;
      }
      fills.moveTo(first.x, first.y);
      for (const p of pts.slice(1)) {
        fills.lineTo(p.x, p.y);
      }
      fills.closePath();
      fills.fill({ color, alpha: 0.12 });
      fills.stroke({ width: 1.6, color, alpha: 0.5 });
    }

    const curves = this.curveG.clear();
    for (const curve of scene.curves) {
      this.paintCurve(curves, curve, view);
    }

    const segs = this.segG.clear();
    for (const seg of scene.segments ?? []) {
      const color = TONE_COLOR[seg.tone];
      const a = this.worldToScreen(seg.a);
      const b = this.worldToScreen(seg.b);
      const width = seg.width ?? 2.2;
      segs.moveTo(a.x, a.y).lineTo(b.x, b.y);
      segs.stroke({ width: width + 7, color, alpha: 0.14 });
      if (seg.dashed === true) {
        dashPolyline(segs, [a, b], 7, 6);
      } else {
        segs.moveTo(a.x, a.y).lineTo(b.x, b.y);
      }
      segs.stroke({ width, color, alpha: 0.95 });
    }

    const marks = this.markG.clear();
    for (const ra of scene.rightAngles ?? []) {
      const v = this.worldToScreen(ra.at);
      const f = this.worldToScreen(ra.from);
      const t = this.worldToScreen(ra.to);
      rightAngleSquare(marks, v, f, t, 11);
      marks.stroke({ width: 1.6, color: TONE_COLOR.gold, alpha: 0.9 });
    }

    this.paintLabels(scene, marks);
  }

  /** Faint integer grid inside the view window. */
  private paintGrid(view: ViewWindow): void {
    const g = this.gridG.clear();
    const stepX = this.tickStep(view.xMax - view.xMin);
    const stepY = this.tickStep(view.yMax - view.yMin);
    for (let x = Math.ceil(view.xMin / stepX) * stepX; x <= view.xMax; x += stepX) {
      const top = this.worldToScreen(pt(x, view.yMax));
      const bottom = this.worldToScreen(pt(x, view.yMin));
      g.moveTo(top.x, top.y).lineTo(bottom.x, bottom.y);
    }
    for (let y = Math.ceil(view.yMin / stepY) * stepY; y <= view.yMax; y += stepY) {
      const left = this.worldToScreen(pt(view.xMin, y));
      const right = this.worldToScreen(pt(view.xMax, y));
      g.moveTo(left.x, left.y).lineTo(right.x, right.y);
    }
    g.stroke({ width: 1, color: COLOR_GRID, alpha: 0.55 });
  }

  /** x/y axes (when inside the view) with arrowheads. */
  private paintAxes(view: ViewWindow): void {
    const g = this.axisG.clear();
    if (view.yMin <= 0 && view.yMax >= 0) {
      const left = this.worldToScreen(pt(view.xMin, 0));
      const right = this.worldToScreen(pt(view.xMax, 0));
      g.moveTo(left.x, left.y).lineTo(right.x, right.y);
      g.moveTo(right.x - 9, right.y - 4.5).lineTo(right.x, right.y).lineTo(right.x - 9, right.y + 4.5);
    }
    if (view.xMin <= 0 && view.xMax >= 0) {
      const bottom = this.worldToScreen(pt(0, view.yMin));
      const top = this.worldToScreen(pt(0, view.yMax));
      g.moveTo(bottom.x, bottom.y).lineTo(top.x, top.y);
      g.moveTo(top.x - 4.5, top.y + 9).lineTo(top.x, top.y).lineTo(top.x + 4.5, top.y + 9);
    }
    g.stroke({ width: 1.6, color: COLOR_AXIS, alpha: 0.95 });
  }

  /** One curve: parabola / line sampled as a polyline, or a vertical line. */
  private paintCurve(g: Graphics, curve: CurveDef, view: ViewWindow): void {
    const color = TONE_COLOR[curve.tone];
    if (curve.kind === "vline") {
      const top = this.worldToScreen(pt(curve.x, view.yMax));
      const bottom = this.worldToScreen(pt(curve.x, view.yMin));
      dashPolyline(g, [top, bottom], 7, 6);
      g.stroke({ width: 1.6, color, alpha: 0.75 });
      return;
    }
    const f =
      curve.kind === "quad"
        ? (x: number): number => evalQuad(curve.quad, x)
        : (x: number): number => evalLine(curve.line, x);
    const marginY = (view.yMax - view.yMin) * 1.5;

    const strokeRun = (run: readonly Pt[]): void => {
      const build = (): void => {
        if (curve.dashed === true) {
          dashPolyline(g, run, 8, 6);
          return;
        }
        const first = run[0];
        if (first === undefined) {
          return;
        }
        g.moveTo(first.x, first.y);
        for (const p of run.slice(1)) {
          g.lineTo(p.x, p.y);
        }
      };
      build();
      g.stroke({ width: 8.5, color, alpha: 0.15 });
      build();
      g.stroke({ width: 2.4, color, alpha: 0.95 });
    };

    let run: Pt[] = [];
    const flush = (): void => {
      if (run.length > 1) {
        strokeRun(run);
      }
      run = [];
    };
    for (let i = 0; i <= CURVE_SAMPLES; i += 1) {
      const x = view.xMin + ((view.xMax - view.xMin) * i) / CURVE_SAMPLES;
      const y = f(x);
      if (y < view.yMin - marginY || y > view.yMax + marginY) {
        flush();
        continue;
      }
      run.push(this.worldToScreen(pt(x, y)));
    }
    flush();
  }

  /** Tick numbers along both axes plus labelled key points. */
  private paintLabels(scene: SceneDef, marks: Graphics): void {
    for (const child of this.labelLayer.children) {
      child.destroy();
    }
    this.labelLayer.removeChildren();

    const view = this.view;
    const addText = (text: string, x: number, y: number, color: number, size: number, bold: boolean): void => {
      const label = new Text({
        text,
        style: {
          fill: color,
          fontSize: size,
          fontFamily: "monospace",
          fontWeight: bold ? "bold" : "normal",
        },
      });
      label.anchor.set(0.5);
      label.position.set(x, y);
      this.labelLayer.addChild(label);
    };

    // Tick numbers on the x axis (skip 0; "O" marks the origin).
    if (view.yMin <= 0 && view.yMax >= 0) {
      const step = this.tickStep(view.xMax - view.xMin);
      for (let x = Math.ceil(view.xMin / step) * step; x <= view.xMax; x += step) {
        if (x === 0) {
          continue;
        }
        const p = this.worldToScreen(pt(x, 0));
        addText(String(x), p.x, p.y + 14, COLOR_TICK, 11, false);
      }
    }
    if (view.xMin <= 0 && view.xMax >= 0) {
      const step = this.tickStep(view.yMax - view.yMin);
      for (let y = Math.ceil(view.yMin / step) * step; y <= view.yMax; y += step) {
        if (y === 0) {
          continue;
        }
        const p = this.worldToScreen(pt(0, y));
        addText(String(y), p.x - 14, p.y, COLOR_TICK, 11, false);
      }
      if (view.yMin <= 0 && view.yMax >= 0) {
        const o = this.worldToScreen(pt(0, 0));
        addText("O", o.x - 12, o.y + 13, COLOR_TICK, 11, false);
      }
    }

    // Key points: dot + label.
    for (const point of scene.points) {
      const p = this.worldToScreen(point.at);
      const emphasis = point.emphasis === true;
      const color = emphasis ? COLOR_EMPHASIS : TONE_COLOR[point.tone ?? "cyan"];
      marks.circle(p.x, p.y, emphasis ? 4.2 : 3.2).fill({ color, alpha: 0.95 });
      if (emphasis) {
        marks.circle(p.x, p.y, 8.5).stroke({ width: 1.4, color, alpha: 0.5 });
      }
      const base = point.text ?? point.name;
      const text =
        point.showCoords === true ? `${base}(${fmtCoord(point.at.x)}, ${fmtCoord(point.at.y)})` : base;
      addText(text, p.x + (point.dx ?? 11), p.y + (point.dy ?? -13), emphasis ? COLOR_EMPHASIS : COLOR_LABEL, emphasis ? 14 : 13, emphasis);
    }
  }
}
